/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { getPublicSiteUrl } from "@cosmos/common-utils";
import { CosmosComponentContext, CosmosComponentContextProps } from "@cosmos/components";
import { EDSProvider, lowDensityThemeClass } from "@eds/react";
import { LocationProvider } from "@epic-mw/app-context";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

interface Props {
	children?: React.ReactNode;
	componentContext?: CosmosComponentContextProps;
}

export const TemplateProviders = ({ children, componentContext }: Props): React.JSX.Element => {
	const [hash, setHash] = useState("");
	useEffect(() => {
		if (typeof window !== "undefined" && window?.location?.hash && hash !== window?.location?.hash) {
			setHash(window.location.hash);
		}
	}, [hash]);
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const search = searchParams.toString();
	const searchStr = search && search.length ? `?${search}` : "";
	// getPublicSiteUrl() returns origin + base path (e.g., "https://www.fab.com/o").
	// usePathname() returns the path relative to basePath (e.g., "/en-US/page"), so no double-prefix.
	const urlStr = `${getPublicSiteUrl()}${pathname}${searchStr}${hash}`;
	const location = new URL(urlStr);

	const content = componentContext ? (
		<CosmosComponentContext.Provider value={componentContext}>{children}</CosmosComponentContext.Provider>
	) : (
		<>{children}</>
	);

	return (
		<EDSProvider dir={"ltr"} themeClass={lowDensityThemeClass}>
			<LocationProvider location={location}>{content}</LocationProvider>
		</EDSProvider>
	);
};
